import React, { useEffect, useState } from 'react';
import { Card } from '../AdminUI';
import { fetchLeads, downloadLeadsCsv } from '../../../lib/api';
import { Phone, Mail, MessageCircle, Search, RefreshCw, Download } from 'lucide-react';
import { toast } from 'sonner';

export default function LeadsSection() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true);
    try { setLeads(await fetchLeads()); } catch (e) { toast.error('Failed to load leads'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = leads.filter((l) => {
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return (
      (l.name || '').toLowerCase().includes(t) ||
      (l.mobile || '').toLowerCase().includes(t) ||
      (l.email || '').toLowerCase().includes(t) ||
      (l.product_name || '').toLowerCase().includes(t) ||
      (l.product_type || '').toLowerCase().includes(t)
    );
  });

  return (
    <Card title="Enquiries & Leads" desc="All user enquiries and interests captured from the website." footer={null}>
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, mobile, product..." className="w-full h-10 pl-9 pr-3 bg-zinc-900 border border-zinc-800 rounded-md text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:border-[color:var(--gold)]/40" data-testid="leads-search-input" />
        </div>
        <button onClick={load} className="h-10 px-3 rounded-md bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200 text-sm flex items-center gap-1.5">
          <RefreshCw size={14} /> Refresh
        </button>
        <button onClick={async () => { try { await downloadLeadsCsv(); toast.success('CSV downloaded'); } catch { toast.error('Download failed'); } }} className="h-10 px-3 rounded-md bg-[color:var(--gold)] hover:brightness-110 text-black font-bold text-sm flex items-center gap-1.5" data-testid="leads-download-csv-btn">
          <Download size={14} /> CSV
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
          <div className="text-zinc-500 text-xs font-semibold">Total Leads</div>
          <div className="mt-1 text-2xl font-bold text-emerald-300">{leads.length}</div>
        </div>
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
          <div className="text-zinc-500 text-xs font-semibold">B2C Enquiries</div>
          <div className="mt-1 text-2xl font-bold text-amber-300">{leads.filter((l) => l.product_type === 'b2c').length}</div>
        </div>
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
          <div className="text-zinc-500 text-xs font-semibold">B2B Enquiries</div>
          <div className="mt-1 text-2xl font-bold text-sky-300">{leads.filter((l) => l.product_type === 'b2b').length}</div>
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm text-center py-8">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-zinc-500 text-sm text-center py-8 border border-dashed border-zinc-800 rounded-md">No enquiries yet.</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((l) => (
            <div key={l._id || l.id} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 hover:border-[color:var(--gold)]/30 transition">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-zinc-100 font-bold text-sm">{l.name || 'Unknown'}</span>
                    {l.product_type && <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-[color:var(--gold)]/15 text-[color:var(--gold)]">{l.product_type}</span>}
                    {l.product_name && <span className="text-zinc-400 text-xs">{l.product_name}</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    <a href={`tel:${l.country || ''}${l.mobile}`} className="text-zinc-400 hover:text-[color:var(--gold)] text-xs flex items-center gap-1">
                      <Phone size={11} /> {l.country || ''} {l.mobile}
                    </a>
                    {l.email && (
                      <a href={`mailto:${l.email}`} className="text-zinc-400 hover:text-[color:var(--gold)] text-xs flex items-center gap-1">
                        <Mail size={11} /> {l.email}
                      </a>
                    )}
                    <a href={`https://wa.me/${(l.country || '').replace('+', '')}${l.mobile}`} target="_blank" rel="noreferrer" className="text-emerald-400 hover:text-emerald-300 text-xs flex items-center gap-1">
                      <MessageCircle size={11} /> WhatsApp
                    </a>
                  </div>
                  {l.note && (
                    <p className="mt-2 text-zinc-300 text-xs bg-zinc-950/50 border border-zinc-800 rounded p-2 max-w-2xl whitespace-pre-wrap">{l.note}</p>
                  )}
                  {l.interest && (
                    <p className="mt-2 text-zinc-400 text-[11px]">Interest: <span className="text-zinc-200">{l.interest.type}</span>{l.interest.platform && ` · ${l.interest.platform}`}</p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[11px] text-zinc-500">{new Date(l.created_at).toLocaleString()}</div>
                  <div className="mt-1 text-[10px] uppercase font-bold tracking-wider text-zinc-500">{l.source || 'website'}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
